import { HttpClient } from '@adapters/HttpClient';
import { Order } from '../models/Orders';
// (imports removidos no utilizados)

// Mapeo de columnas del frontend (AgGrid field) a columnas del backend (export API)
const COLUMN_MAPPING: Record<string, string> = {
  ref: 'ref',
  total: 'total',
  shop: 'shop',
  order_created_at: 'creation_date',
  ready_to_prepare_at: 'ready_to_prepare_at',
  status: 'status',
  courier: 'courier',
  customer: 'customer_name',
  shipment_processed_at: 'processed_at',
  logistic_status: 'tracking_stage',
  tracking_number: 'tracking_number',
  date_tracking_update: 'date_tracking_update',
  payment_method: 'payment_method',
  phone: 'shipping_phone',
  postal_code: 'postal_code',
  team: 'team',
  preparation: 'preparation',
};

// Columnas por defecto para exportación (todas las disponibles)
const DEFAULT_EXPORT_COLUMNS = [
  'id',
  'ref',
  'shop',
  'customer_name',
  'status',
  'tracking_stage',
  'total',
  'payment_method',
  'creation_date',
  'ready_to_prepare_at',
  'processed_at',
  'client_country',
  'address',
  'shipping_phone',
  'postal_code',
  'date_tracking_update',
  'external_financial_status',
  'courier',
  'tracking_number',
  'team',
  'preparation',
];

// Función para mapear columnas del frontend al backend
function mapVisibleColumnsToExport(visibleColumns: string[]): string[] {
  if (!visibleColumns || visibleColumns.length === 0) {
    return DEFAULT_EXPORT_COLUMNS;
  }

  const mappedColumns = new Set<string>();
  // Siempre incluir 'id' como primera columna
  mappedColumns.add('id');

  visibleColumns.forEach((frontendCol) => {
    const backendCol = COLUMN_MAPPING[frontendCol];
    if (backendCol) {
      mappedColumns.add(backendCol);
    }
  });

  // Si no se mapeó ninguna columna válida, usar las por defecto
  if (mappedColumns.size <= 1) {
    return DEFAULT_EXPORT_COLUMNS;
  }

  return Array.from(mappedColumns);
}

export interface OrdersFilters {
  search?: string;
  searchFilter?: { filters: any[]; logic: string };
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
  page?: number;
  limit?: number;
  status_ids?: number | number[];
  has_order_errors?: 1;
  general_error_ids?: string[];
}

export interface ExportOrdersPayload {
  search?: string;
  advanced_filters?: {
    filters: Array<{
      column: string;
      operator: string;
      value?: any;
    }>;
    logic: string;
  };
  sort_by?: string;
  sort_direction?: 'asc' | 'desc';
  status_ids?: number | number[];
}

export interface ExportOrdersResponse {
  url_file: string;
}

export interface ExportOrdersBody {
  columns: string[];
}

export interface OrderDetailsFilters {
  ids?: number[];
  page?: number;
  per_page?: number;
}

export interface OrdersPage {
  orders: Order[];
  nextPage?: number;
  hasMore: boolean;
  total: number;
}

/** Snapshot de estado logístico (respuesta API courier / pedido). */
export interface LogisticsSnapshot {
  shipment_status_mapped_id: number | null;
  date_tracking_update: string | null;
  last_shipment_update: string | null;
  shipment_status_mapped:
    | {
        id: number;
        status_code: string;
        status_description: string;
      }
    | { id: number }
    | null;
  shipment_status: { id: number; name: string } | null;
}

/** Respuesta del endpoint de refresco de tracking desde el courier (panel 3PL). */
export interface RefreshOrderLogisticsResponse {
  success: boolean;
  message: string;
  detail?: {
    order_id?: number;
    courier_id?: number;
    courier_integration_id?: number;
    tracking_number?: string;
    tracking_api_call?: unknown;
    previous_values?: LogisticsSnapshot;
    updated_values?: LogisticsSnapshot;
    current_values?: LogisticsSnapshot;
    [key: string]: unknown;
  };
}

export function logisticsSnapshotsEqual(a?: LogisticsSnapshot | null, b?: LogisticsSnapshot | null): boolean {
  if (!a || !b) return false;
  return (
    a.shipment_status_mapped_id === b.shipment_status_mapped_id &&
    a.date_tracking_update === b.date_tracking_update &&
    a.last_shipment_update === b.last_shipment_update
  );
}

function buildAdvancedFiltersParams(searchFilter: { filters: any[]; logic: string }) {
  const params: Record<string, any> = {};
  if (searchFilter.filters && Array.isArray(searchFilter.filters)) {
    searchFilter.filters.forEach((f, i) => {
      params[`advanced_filters[filters][${i}][column]`] = f.column;
      params[`advanced_filters[filters][${i}][operator]`] = f.operator;
      // Solo enviar value si existe (para operadores is_null / is_not_null no debe enviarse)
      if (f.value !== undefined && f.value !== null && !(Array.isArray(f.value) && f.value.length === 0)) {
        params[`advanced_filters[filters][${i}][value]`] = f.value;
      }
    });
  }
  if (searchFilter.logic) {
    params['advanced_filters[logic]'] = searchFilter.logic;
  }
  return params;
}

// 🎯 Construye los query params para exportación como string
function buildExportQueryString(payload: ExportOrdersPayload): string {
  const params: Record<string, any> = {};

  // Agregar parámetro de búsqueda simple
  if (payload.search) {
    params.search = payload.search;
  }

  if (payload.advanced_filters) {
    const af = buildAdvancedFiltersParams(payload.advanced_filters as any);
    Object.assign(params, af);
  }

  const sortBy = payload.sort_by || 'order_created_at';
  const sortDirection = payload.sort_direction || 'desc';
  params['sorting[column]'] = sortBy;
  params['sorting[direction]'] = sortDirection;

  if (payload.status_ids !== undefined && payload.status_ids !== null) {
    params.status_ids = Array.isArray(payload.status_ids) ? payload.status_ids.join(',') : payload.status_ids;
  }

  // Construir query string
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      searchParams.append(key, String(value));
    }
  });

  return searchParams.toString();
}

export class OrdersApiAdapter {
  constructor(private httpClient: HttpClient) {}

  async getOrdersPaginated(filters: OrdersFilters = {}): Promise<OrdersPage> {
    const {
      search,
      searchFilter,
      sortBy = 'order_created_at',
      sortDirection = 'desc',
      page = 1,
      limit = 15,
      status_ids,
      has_order_errors,
      general_error_ids,
    } = filters;

    const params: any = {
      ...(sortBy && { 'sorting[column]': sortBy }),
      ...(sortDirection && { 'sorting[direction]': sortDirection }),
      page,
      per_page: limit,
      ...(status_ids && {
        status_ids: Array.isArray(status_ids) ? status_ids.join(',') : status_ids,
      }),
      ...(search && { search }),
      ...(searchFilter ? buildAdvancedFiltersParams(searchFilter) : {}),
      ...(has_order_errors && { has_order_errors }),
      ...(general_error_ids?.length && { general_error_ids: general_error_ids.join(',') }),
    };

    // Solo mostrar loading global para la primera página
    const showGlobalLoading = page === 1;
    const response = await this.httpClient.get<any>('/orders', params, {}, showGlobalLoading);

    return {
      orders: response.data,
      nextPage: response.meta.currentPage < response.meta.lastPage ? response.meta.currentPage + 1 : undefined,
      hasMore: response.meta.currentPage < response.meta.lastPage,
      total: response.meta.total,
    };
  }

  async getAll(): Promise<Order[]> {
    const response = await this.getOrdersPaginated();
    return response.orders;
  }

  async getOrderById(id: number): Promise<Order> {
    const response = await this.httpClient.get<Order>(`/orders/${id}`);
    return response;
  }

  async getOrdersByIdsPaginated(filters: OrderDetailsFilters = {}): Promise<OrdersPage> {
    const { ids, page = 1, per_page = 100 } = filters;

    const params: any = {
      page,
      per_page,
      ids: ids.join(','),
    };

    // Solo mostrar loading global para la primera página
    const showGlobalLoading = page === 1;
    const response = await this.httpClient.get<any>('/orders', params, {}, showGlobalLoading);

    return {
      orders: response.data,
      nextPage: response.meta.currentPage < response.meta.lastPage ? response.meta.currentPage + 1 : undefined,
      hasMore: response.meta.currentPage < response.meta.lastPage,
      total: response.meta.total,
    };
  }

  async cancelOrder(id: number): Promise<void> {
    await this.httpClient.put(`/orders/${id}/cancel`, {});
  }

  async uncancelOrder(id: number): Promise<void> {
    await this.httpClient.put(`/orders/${id}/uncancel`, {});
  }

  async markAsPreparation(ids: number[], preparationType: number = 1): Promise<void> {
    await this.httpClient.put('/orders/mark-as-preparation', {
      ids,
      preparation_type: preparationType,
    });
  }

  async confirmOrder(id: number): Promise<void> {
    await this.httpClient.put(`/orders/${id}/confirm`, {});
  }

  async confirmOrders(ids: number[]): Promise<void> {
    // Si es un solo ID, usar el endpoint individual
    if (ids.length === 1) {
      await this.confirmOrder(ids[0]);
      return;
    }

    // Para múltiples IDs, usar el endpoint de batch
    await this.httpClient.put('/orders/confirm', {
      ids,
    });
  }

  async updateFromShop(id: number): Promise<void> {
    await this.httpClient.put(`/orders/${id}/update-from-shop`, {});
  }

  async unassignStock(id: number): Promise<void> {
    await this.httpClient.put(`/orders/${id}/unassign-stock`, {});
  }

  async cancelBulkOrders(ids: number[]): Promise<void> {
    await this.httpClient.put('/orders/cancel-bulk', {
      ids,
    });
  }

  async uncancelBulkOrders(ids: number[]): Promise<void> {
    await this.httpClient.put('/orders/uncancel-bulk', {
      ids,
    });
  }

  async confirmBulkOrders(ids: number[]): Promise<void> {
    await this.httpClient.put('/orders/confirm-bulk', {
      ids,
    });
  }

  async printOrder(orderId: number, printingType: string, printerCode?: string): Promise<{ link: string }> {
    const params: any = {
      printing_type: printingType,
    };

    if (printerCode) {
      params.printer_code = printerCode;
    }

    return await this.httpClient.get(`/orders/${orderId}/print`, params);
  }

  /**
   * Consulta la API del transportista y actualiza el pedido con el último evento de seguimiento.
   * Puede tardar varios segundos (llamada externa).
   */
  async refreshOrderLogistics(orderId: number): Promise<RefreshOrderLogisticsResponse> {
    return await this.httpClient.post<RefreshOrderLogisticsResponse>(
      '/tracking/api-calls/orders/refresh-logistics',
      { order_id: orderId },
      true,
      { timeout: 120_000 },
    );
  }

  async exportOrders(payload: ExportOrdersPayload, visibleColumns?: string[]): Promise<ExportOrdersResponse> {
    const queryString = buildExportQueryString(payload);
    const url = `/orders/export${queryString ? `?${queryString}` : ''}`;
    const columns = mapVisibleColumnsToExport(visibleColumns || []);
    const body: ExportOrdersBody = { columns };
    return await this.httpClient.post(url, body);
  }

  /**
   * Obtiene el recuento de pedidos agrupados por status
   * Respeta los filtros aplicados (búsqueda y filtros avanzados)
   */
  async getOrdersCountByStatus(filters: OrdersFilters = {}): Promise<Record<string, number>> {
    const params: Record<string, any> = {};

    if (filters.search) {
      params.search = filters.search;
    }

    if (filters.searchFilter) {
      const af = buildAdvancedFiltersParams(filters.searchFilter);
      Object.assign(params, af);
    }

    if (filters.has_order_errors) {
      params.has_order_errors = filters.has_order_errors;
    }

    if (filters.general_error_ids?.length) {
      params.general_error_ids = filters.general_error_ids.join(',');
    }

    try {
      const response = await this.httpClient.get<Record<string, number>>('/orders/count-by-status', params);
      return response;
    } catch (error) {
      console.error('Error fetching orders count by status:', error);
      return {};
    }
  }
}
